import React, { useState, useEffect } from 'react';
import { useReports } from '../contexts/ReportsContext';
import { useQueueRouting } from '../contexts/QueueRoutingContext';
import { useUser } from '../contexts/UserContext';
import { useAgentStatus } from '../contexts/AgentStatusContext';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  Chip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Divider,
  Fade,
  TextField,
  InputAdornment,
  Tooltip,
  LinearProgress,
  Badge,
  Alert,
  Snackbar,
} from '@mui/material';
import Grid2 from '@mui/material/Grid';
import {
  Phone as PhoneIcon,
  PhoneCallback as PhoneCallbackIcon,
  PhoneInTalk as PhoneInTalkIcon,
  PhoneMissed as PhoneMissedIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  CallEnd as CallEndIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  RecordVoiceOver as RecordIcon,
  Timer as TimerIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  SignalCellular4Bar as SignalIcon,
  PauseCircle as PauseCircleIcon,
  LocationOn as LocationOnIcon,
  Star as StarIcon,
  SupervisorAccount as SupervisorIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  Notifications as NotificationsIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';

const SupervisorPanel: React.FC = () => {
  const reportsContext = useReports();
  const queueRoutingContext = useQueueRouting();
  const userContext = useUser();
  const agentStatusContext = useAgentStatus();
  
  const { 
    dailyStats, 
    realtimeStats, 
    activeAgents, 
    waitingCalls, 
    queuePerformance, 
    hourlyDistribution,
    loading, 
    error, 
    fetchAllReports,
    refreshReports 
  } = reportsContext;
  
  const { activeCalls, fetchActiveCalls } = queueRoutingContext;
  const { users } = userContext;
  const { statuses } = agentStatusContext;
  
  const [supervisorStatus, setSupervisorStatus] = useState('online');
  const [statusDuration, setStatusDuration] = useState('02:45');
  const [sipStatus, setSipStatus] = useState('bağlı');
  const [activeTab, setActiveTab] = useState('agentler');
  const [selectedAgent, setSelectedAgent] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' as 'success' | 'error' | 'warning' | 'info' });

  // Load data on component mount
  useEffect(() => {
    fetchAllReports();
    fetchActiveCalls();
    
    // Refresh data every 30 seconds
    const interval = setInterval(() => {
      refreshReports();
      fetchActiveCalls();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  // Status duration timer
  useEffect(() => {
    let startTime = Date.now();
    const interval = setInterval(() => {
      const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
      const minutes = String(Math.floor(elapsedSeconds / 60)).padStart(2, '0');
      const seconds = String(elapsedSeconds % 60).padStart(2, '0');
      setStatusDuration(`${minutes}:${seconds}`);
    }, 1000);

    return () => clearInterval(interval);
  }, [supervisorStatus]);

  const getStatusText = (status: string) => {
    switch (status) {
      case 'online': return 'Çevrimiçi';
      case 'busy': return 'Meşgul';
      case 'away': return 'Uzakta';
      case 'offline': return 'Çevrimdışı';
      default: return 'Bilinmiyor';
    }
  };

  const getSipStatusColor = (status: string) => {
    return status === 'bağlı' ? '#4CAF50' : '#F44336';
  };

  // Get agent name by ID
  const getAgentName = (agentId: string) => {
    const agent = users.find(user => user._id === agentId);
    return agent ? `${agent.firstName} ${agent.lastName}` : 'Bilinmiyor';
  };

  // Get agent status info
  const getAgentStatusInfo = (agentId: string) => {
    const agent = users.find(user => user._id === agentId);
    if (!agent) return null;
    
    const status = statuses.find(s => s._id === agent.currentStatusId);
    return status;
  };

  // Format duration
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Get statistics from real data
  const statistics = {
    totalCalls: dailyStats?.totalCalls || 0,
    successfulCalls: dailyStats?.successfulCalls || 0,
    missedCalls: dailyStats?.missedCalls || 0,
    averageCallDuration: dailyStats?.averageCallDuration || '0:00',
    totalAgents: users.length,
    activeAgents: activeAgents.length,
    successRate: dailyStats?.successRate || 0
  };

  // Get alerts from real data
  const alerts = [
    ...(waitingCalls.length > 5 ? [{
      id: 'waiting-calls',
      type: 'warning',
      message: `${waitingCalls.length} çağrı bekliyor`,
      time: 'Şimdi'
    }] : []),
    ...(activeAgents.filter(agent => agent.status === 'offline').length > 0 ? [{
      id: 'offline-agents',
      type: 'error',
      message: `${activeAgents.filter(agent => agent.status === 'offline').length} temsilci çevrimdışı`,
      time: 'Şimdi'
    }] : []),
    ...(queuePerformance.filter(queue => queue.averageWaitTime > 300).map(queue => ({
      id: `queue-${queue._id}`,
      type: 'warning',
      message: `${queue.name} kuyruğunda bekleme süresi uzun`,
      time: 'Şimdi'
    })))
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: '#F5F5F5',
      display: 'flex'
    }}>
      {/* Sol Menü */}
      <Box sx={{
        width: 300,
        bgcolor: 'white',
        borderRight: '1px solid #E0E0E0',
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        position: 'sticky',
          top: 0,
        zIndex: 100
      }}>
        {/* Statü Dropdown */}
        <Box sx={{ p: 3, borderBottom: '1px solid #E0E0E0', bgcolor: '#1976D2' }}>
          <Box sx={{ 
            width: '100%',
            bgcolor: 'rgba(255,255,255,0.2)',
            borderRadius: 2,
            p: 2,
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.3)'
            },
            transition: 'all 0.2s ease'
          }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body1" fontWeight="500" color="white">
                {getStatusText(supervisorStatus)}
              </Typography>
              <Typography variant="body2" color="white" sx={{ opacity: 0.9 }}>
                {statusDuration}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Hızlı İstatistikler */}
        <Box sx={{ p: 3, borderBottom: '1px solid #E0E0E0' }}>
          <Typography variant="body2" color="#666" sx={{ mb: 2, fontWeight: '500' }}>
            Hızlı İstatistikler
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="#666">Toplam Çağrı</Typography>
              <Typography variant="body2" fontWeight="600" color="#1976D2">{statistics.totalCalls}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="#666">Başarı Oranı</Typography>
              <Typography variant="body2" fontWeight="600" color="#4CAF50">{statistics.successRate}%</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="#666">Aktif Temsilci</Typography>
              <Typography variant="body2" fontWeight="600" color="#FF9800">{statistics.activeAgents}/{statistics.totalAgents}</Typography>
            </Box>
          </Box>
        </Box>

        {/* Uyarılar */}
        <Box sx={{ p: 3, borderBottom: '1px solid #E0E0E0', flex: 1 }}>
          <Typography variant="body2" color="#666" sx={{ mb: 2, fontWeight: '500' }}>
            Uyarılar
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {alerts.map((alert) => (
              <Box key={alert.id} sx={{
                p: 1.5,
                bgcolor: alert.type === 'warning' ? '#FFF3E0' : 
                        alert.type === 'error' ? '#FFEBEE' : '#E3F2FD',
                borderRadius: 1,
                border: `1px solid ${alert.type === 'warning' ? '#FF9800' : 
                                        alert.type === 'error' ? '#F44336' : '#2196F3'}`,
                borderLeft: `4px solid ${alert.type === 'warning' ? '#FF9800' : 
                                            alert.type === 'error' ? '#F44336' : '#2196F3'}`
              }}>
                <Typography variant="caption" color="#666" sx={{ mb: 0.5 }}>
                  {alert.time}
                </Typography>
                <Typography variant="body2" color="#333">
                  {alert.message}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* SIP Durumu */}
        <Box sx={{ p: 3, borderTop: '1px solid #E0E0E0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <SignalIcon sx={{ fontSize: 16, color: getSipStatusColor(sipStatus) }} />
            <Typography variant="body2" color="#666">
              Softphone
            </Typography>
            <Chip
              label={sipStatus}
              size="small"
              sx={{
                bgcolor: getSipStatusColor(sipStatus),
                color: 'white',
                fontWeight: '500',
                fontSize: '0.75rem',
                height: 20
              }}
            />
          </Box>
            </Box>

        {/* Supervisor Bilgileri */}
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{
              width: 50,
              height: 50,
              bgcolor: '#1976D2',
              fontSize: '1.2rem',
              fontWeight: 'bold'
            }}>
              SY
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="600" color="#333">
                Selin Yılmaz
              </Typography>
              <Typography variant="body2" color="#666">
                Supervisor
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Ana İçerik Alanı */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
        {/* Header */}
        <Box sx={{
          p: 3,
          bgcolor: 'white',
          borderBottom: '1px solid #E0E0E0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: 1200, mx: 'auto' }}>
            {/* Sol taraf - Arama */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                placeholder="Temsilci ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                sx={{
                  width: 200,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    fontSize: '0.875rem',
                    '& fieldset': {
                      borderColor: '#E0E0E0',
                    },
                    '&:hover fieldset': {
                      borderColor: '#1976D2',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1976D2',
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#666', fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<FilterIcon />}
                sx={{
                  borderColor: '#1976D2',
                  color: '#1976D2',
                  '&:hover': { borderColor: '#1565C0', bgcolor: 'rgba(25, 118, 210, 0.04)' }
                }}
              >
                Filtrele
              </Button>
            </Box>

            {/* Orta - Başlık */}
            <Typography variant="h5" fontWeight="600" color="#333">
              Supervisor Panel
            </Typography>

            {/* Sağ taraf - Aksiyonlar */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Tooltip title="Yenile">
                <IconButton
                  sx={{
                    bgcolor: '#4CAF50',
                    color: 'white',
                    '&:hover': { bgcolor: '#45A049' },
                    width: 40,
                    height: 40
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Rapor İndir">
                <IconButton
                  sx={{
                    bgcolor: '#FF9800',
                    color: 'white',
                    '&:hover': { bgcolor: '#F57C00' },
                    width: 40,
                    height: 40
                  }}
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Bildirimler">
                <IconButton
                  sx={{
                    bgcolor: '#9C27B0',
                    color: 'white',
                    '&:hover': { bgcolor: '#7B1FA2' },
                    width: 40,
                    height: 40
                  }}
                >
                  <Badge badgeContent={3} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {/* Ana İçerik */}
        <Box sx={{ flex: 1, p: 3 }}>
          <Fade in timeout={500}>
            <Card sx={{
              borderRadius: 2,
              bgcolor: 'white',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              border: '1px solid #E0E0E0',
              height: '100%'
            }}>
              <CardContent sx={{ p: 3 }}>
                {/* Menü */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', gap: 1, borderBottom: '1px solid #E0E0E0' }}>
                    <Box
                      sx={{
                        px: 3,
                        py: 1.5,
                        cursor: 'pointer',
                        borderBottom: activeTab === 'agentler' ? '2px solid #1976D2' : '2px solid transparent',
                        color: activeTab === 'agentler' ? '#1976D2' : '#666',
                        fontWeight: activeTab === 'agentler' ? 600 : 500,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          color: '#1976D2',
                          bgcolor: 'rgba(25, 118, 210, 0.04)'
                        }
                      }}
                      onClick={() => setActiveTab('agentler')}
                    >
                      Temsilciler
                    </Box>
                    <Box
                      sx={{
                        px: 3,
                        py: 1.5,
                        cursor: 'pointer',
                        borderBottom: activeTab === 'istatistikler' ? '2px solid #1976D2' : '2px solid transparent',
                        color: activeTab === 'istatistikler' ? '#1976D2' : '#666',
                        fontWeight: activeTab === 'istatistikler' ? 600 : 500,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          color: '#1976D2',
                          bgcolor: 'rgba(25, 118, 210, 0.04)'
                        }
                      }}
                      onClick={() => setActiveTab('istatistikler')}
                    >
                      İstatistikler
                    </Box>
                    <Box
                      sx={{
                        px: 3,
                        py: 1.5,
                        cursor: 'pointer',
                        borderBottom: activeTab === 'raporlar' ? '2px solid #1976D2' : '2px solid transparent',
                        color: activeTab === 'raporlar' ? '#1976D2' : '#666',
                        fontWeight: activeTab === 'raporlar' ? 600 : 500,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          color: '#1976D2',
                          bgcolor: 'rgba(25, 118, 210, 0.04)'
                        }
                      }}
                      onClick={() => setActiveTab('raporlar')}
                    >
                      Raporlar
                    </Box>
                    <Box
                      sx={{
                        px: 3,
                        py: 1.5,
                        cursor: 'pointer',
                        borderBottom: activeTab === 'ayarlar' ? '2px solid #1976D2' : '2px solid transparent',
                        color: activeTab === 'ayarlar' ? '#1976D2' : '#666',
                        fontWeight: activeTab === 'ayarlar' ? 600 : 500,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          color: '#1976D2',
                          bgcolor: 'rgba(25, 118, 210, 0.04)'
                        }
                      }}
                      onClick={() => setActiveTab('ayarlar')}
                    >
                      Ayarlar
                    </Box>
                  </Box>
                </Box>

                {/* İçerik */}
                <Box>
                  {activeTab === 'agentler' && (
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h6" fontWeight="600" color="#333">
                          Temsilci Listesi
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          sx={{
                            bgcolor: '#1976D2',
                            '&:hover': { bgcolor: '#1565C0' }
                          }}
                        >
                          Yeni Temsilci
                        </Button>
      </Box>

                      <Grid2 container spacing={2}>
                        {activeAgents.map((agent) => {
                          const agentInfo = users.find(user => user._id === agent._id);
                          const statusInfo = getAgentStatusInfo(agent._id);
                          const currentCall = activeCalls.find(call => call.agentId === agent._id);
                          
                          return (
                            <Grid2 size={{ xs: 12, md: 6, lg: 4 }} key={agent._id}>
                              <Card sx={{
                                borderRadius: 2,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                border: '1px solid #E0E0E0',
                                '&:hover': {
                                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                                  transform: 'translateY(-2px)'
                                },
                                transition: 'all 0.2s ease'
                              }}>
                                <CardContent sx={{ p: 2 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                      <Avatar sx={{
                                        width: 40,
                                        height: 40,
                                        bgcolor: statusInfo?.color || '#666'
                                      }}>
                                        {agentInfo ? agentInfo.firstName[0] + agentInfo.lastName[0] : 'A'}
                                      </Avatar>
                                      <Box>
                                        <Typography variant="subtitle1" fontWeight="600" color="#333">
                                          {getAgentName(agent._id)}
                                        </Typography>
                                        <Chip
                                          label={statusInfo?.displayName || 'Bilinmiyor'}
                                          size="small"
                                          sx={{
                                            bgcolor: statusInfo?.color || '#666',
                                            color: 'white',
                                            fontSize: '0.7rem',
                                            height: 20
                                          }}
                                        />
                                      </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                      <Tooltip title="Düzenle">
                                        <IconButton size="small">
                                          <EditIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Sil">
                                        <IconButton size="small">
                                          <DeleteIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  </Box>

                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                      <Typography variant="body2" color="#666">Çağrı Sayısı</Typography>
                                      <Typography variant="body2" fontWeight="600">{agent.totalCalls || 0}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                      <Typography variant="body2" color="#666">Başarı Oranı</Typography>
                                      <Typography variant="body2" fontWeight="600" color="#4CAF50">{agent.successRate || 0}%</Typography>
                                    </Box>
                                    {currentCall && (
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="body2" color="#666">Mevcut Çağrı</Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          <PhoneInTalkIcon sx={{ fontSize: 14, color: '#4CAF50' }} />
                                          <Typography variant="body2" fontWeight="600" color="#4CAF50">
                                            {currentCall.callerNumber}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    )}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                      <Typography variant="body2" color="#666">Durum Süresi</Typography>
                                      <Typography variant="body2" fontWeight="600">{agent.statusDuration || '0:00'}</Typography>
                                    </Box>
                                  </Box>

                                  <Box sx={{ mt: 2 }}>
                                    <LinearProgress
                                      variant="determinate"
                                      value={agent.successRate || 0}
                                      sx={{
                                        height: 6,
                                        borderRadius: 3,
                                        bgcolor: '#E0E0E0',
                                        '& .MuiLinearProgress-bar': {
                                          bgcolor: (agent.successRate || 0) >= 80 ? '#4CAF50' : 
                                                  (agent.successRate || 0) >= 60 ? '#FF9800' : '#F44336'
                                        }
                                      }}
                                    />
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid2>
                          );
                        })}
          </Grid2>
                    </Box>
                  )}

                  {activeTab === 'istatistikler' && (
                    <Box>
                      <Typography variant="h6" fontWeight="600" sx={{ mb: 3, color: '#333' }}>
                        Genel İstatistikler
                      </Typography>

                      {/* Ana İstatistik Kartları */}
                      <Grid2 container spacing={3} sx={{ mb: 4 }}>
                        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                          <Card sx={{
                            borderRadius: 2,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            border: '1px solid #E0E0E0',
                            textAlign: 'center',
                            p: 2
                          }}>
                            <Typography variant="h4" fontWeight="bold" color="#1976D2" sx={{ mb: 1 }}>
                              {statistics.totalCalls}
                  </Typography>
                            <Typography variant="body2" color="#666">
                              Toplam Çağrı
                  </Typography>
              </Card>
          </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                          <Card sx={{
                            borderRadius: 2,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            border: '1px solid #E0E0E0',
                            textAlign: 'center',
                            p: 2
                          }}>
                            <Typography variant="h4" fontWeight="bold" color="#4CAF50" sx={{ mb: 1 }}>
                              {statistics.successfulCalls}
                  </Typography>
                            <Typography variant="body2" color="#666">
                              Başarılı Çağrı
                  </Typography>
              </Card>
          </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                          <Card sx={{
                            borderRadius: 2,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            border: '1px solid #E0E0E0',
                            textAlign: 'center',
                            p: 2
                          }}>
                            <Typography variant="h4" fontWeight="bold" color="#FF9800" sx={{ mb: 1 }}>
                              {statistics.successRate}%
                  </Typography>
                            <Typography variant="body2" color="#666">
                    Başarı Oranı
                  </Typography>
                          </Card>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                          <Card sx={{
                            borderRadius: 2,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            border: '1px solid #E0E0E0',
                            textAlign: 'center',
                            p: 2
                          }}>
                            <Typography variant="h4" fontWeight="bold" color="#9C27B0" sx={{ mb: 1 }}>
                              {statistics.averageCallDuration}
                            </Typography>
                            <Typography variant="body2" color="#666">
                              Ort. Süre
                            </Typography>
              </Card>
          </Grid2>
        </Grid2>

                      {/* Detaylı Grafikler */}
                      <Grid2 container spacing={3}>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                          <Card sx={{
                            borderRadius: 2,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            border: '1px solid #E0E0E0',
                            p: 3
                          }}>
                            <Typography variant="h6" fontWeight="600" sx={{ mb: 2, color: '#333' }}>
                              Temsilci Performansı
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              {activeAgents.map((agent: any) => {
                                const agentInfo = users.find((user: any) => user._id === agent._id);
                                return (
                                  <Box key={agent._id}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                      <Typography variant="body2" color="#666">
                                        {agentInfo ? `${agentInfo.firstName} ${agentInfo.lastName}` : 'Bilinmiyor'}
                                      </Typography>
                                      <Typography variant="body2" fontWeight="600">{agent.successRate || 0}%</Typography>
                                    </Box>
                                    <LinearProgress
                                      variant="determinate"
                                      value={agent.successRate || 0}
                                      sx={{
                                        height: 8,
                                        borderRadius: 4,
                                        bgcolor: '#E0E0E0',
                                        '& .MuiLinearProgress-bar': {
                                          bgcolor: (agent.successRate || 0) >= 80 ? '#4CAF50' : 
                                                  (agent.successRate || 0) >= 60 ? '#FF9800' : '#F44336'
                                        }
                                      }}
                                    />
                                  </Box>
                                );
                              })}
                            </Box>
                          </Card>
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                          <Card sx={{
                            borderRadius: 2,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            border: '1px solid #E0E0E0',
                            p: 3
                          }}>
                            <Typography variant="h6" fontWeight="600" sx={{ mb: 2, color: '#333' }}>
                              Çağrı Dağılımı
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" color="#666">Başarılı Çağrılar</Typography>
                                <Typography variant="body2" fontWeight="600" color="#4CAF50">
                                  {statistics.successfulCalls} (%{Math.round((statistics.successfulCalls / statistics.totalCalls) * 100)})
              </Typography>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={(statistics.successfulCalls / statistics.totalCalls) * 100}
                                sx={{
                                  height: 8,
                                  borderRadius: 4,
                                  bgcolor: '#E0E0E0',
                                  '& .MuiLinearProgress-bar': {
                                    bgcolor: '#4CAF50'
                                  }
                                }}
                              />
                              
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" color="#666">Cevapsız Çağrılar</Typography>
                                <Typography variant="body2" fontWeight="600" color="#F44336">
                                  {statistics.missedCalls} (%{Math.round((statistics.missedCalls / statistics.totalCalls) * 100)})
                              </Typography>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={(statistics.missedCalls / statistics.totalCalls) * 100}
                                sx={{
                                  height: 8,
                                  borderRadius: 4,
                                  bgcolor: '#E0E0E0',
                                  '& .MuiLinearProgress-bar': {
                                    bgcolor: '#F44336'
                                  }
                                }}
                              />
                            </Box>
                          </Card>
                        </Grid2>
                      </Grid2>
                          </Box>
                  )}

                  {activeTab === 'raporlar' && (
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h6" fontWeight="600" color="#333">
                          Raporlar
                          </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<DownloadIcon />}
                            sx={{
                              borderColor: '#1976D2',
                              color: '#1976D2',
                              '&:hover': { borderColor: '#1565C0', bgcolor: 'rgba(25, 118, 210, 0.04)' }
                            }}
                          >
                            Excel İndir
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<DownloadIcon />}
                            sx={{
                              borderColor: '#4CAF50',
                              color: '#4CAF50',
                              '&:hover': { borderColor: '#45A049', bgcolor: 'rgba(76, 175, 80, 0.04)' }
                            }}
                          >
                            PDF İndir
                          </Button>
                        </Box>
                        </Box>
                        
                      {/* Rapor Filtreleri */}
                      <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <CardContent sx={{ p: 2 }}>
                          <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2, color: '#333' }}>
                            Rapor Filtreleri
                          </Typography>
                          <Grid2 container spacing={2}>
                            <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                              <FormControl size="small" fullWidth>
                                <InputLabel>Rapor Tipi</InputLabel>
                                <Select
                                  defaultValue="gunluk"
                                  label="Rapor Tipi"
                                  sx={{ borderRadius: 1 }}
                                >
                                  <MenuItem value="gunluk">Günlük Rapor</MenuItem>
                                  <MenuItem value="haftalik">Haftalık Rapor</MenuItem>
                                  <MenuItem value="aylik">Aylık Rapor</MenuItem>
                                  <MenuItem value="ozel">Özel Tarih Aralığı</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid2>
                            <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                              <FormControl size="small" fullWidth>
                                <InputLabel>Temsilci</InputLabel>
                                <Select
                                  defaultValue="tumu"
                                  label="Temsilci"
                                  sx={{ borderRadius: 1 }}
                                >
                                  <MenuItem value="tumu">Tüm Temsilciler</MenuItem>
                                  <MenuItem value="ahmet">Ahmet Yılmaz</MenuItem>
                                  <MenuItem value="ayse">Ayşe Demir</MenuItem>
                                  <MenuItem value="mehmet">Mehmet Kaya</MenuItem>
                                  <MenuItem value="zeynep">Zeynep Özkan</MenuItem>
                                  <MenuItem value="can">Can Yıldız</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid2>
                            <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                              <TextField
                                label="Başlangıç Tarihi"
                                type="date"
                                size="small"
                                fullWidth
                                defaultValue="2024-12-15"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                              />
                            </Grid2>
                            <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                              <TextField
                                label="Bitiş Tarihi"
                                type="date"
                                size="small"
                                fullWidth
                                defaultValue="2024-12-15"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                              />
                            </Grid2>
                          </Grid2>
                          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<SearchIcon />}
                              sx={{
                                bgcolor: '#1976D2',
                                '&:hover': { bgcolor: '#1565C0' }
                              }}
                            >
                              Rapor Oluştur
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<RefreshIcon />}
                              sx={{
                                borderColor: '#666',
                                color: '#666',
                                '&:hover': { borderColor: '#333', bgcolor: 'rgba(0,0,0,0.04)' }
                              }}
                            >
                              Temizle
                            </Button>
                        </Box>
                      </CardContent>
                      </Card>

                      {/* Rapor Özeti */}
                      <Grid2 container spacing={3} sx={{ mb: 3 }}>
                        <Grid2 size={{ xs: 12, md: 4 }}>
                          <Card sx={{
                            borderRadius: 2,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            border: '1px solid #E0E0E0',
                            p: 2,
                            textAlign: 'center'
                          }}>
                            <Typography variant="h4" fontWeight="bold" color="#1976D2" sx={{ mb: 1 }}>
                              156
                            </Typography>
                            <Typography variant="body2" color="#666">
                              Toplam Çağrı
                            </Typography>
                            <Typography variant="caption" color="#4CAF50" sx={{ display: 'block', mt: 0.5 }}>
                              ↑ %12 artış
                            </Typography>
                    </Card>
                  </Grid2>
                        <Grid2 size={{ xs: 12, md: 4 }}>
                          <Card sx={{
                            borderRadius: 2,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            border: '1px solid #E0E0E0',
                            p: 2,
                            textAlign: 'center'
                          }}>
                            <Typography variant="h4" fontWeight="bold" color="#4CAF50" sx={{ mb: 1 }}>
                              82%
                            </Typography>
                            <Typography variant="body2" color="#666">
                              Başarı Oranı
                            </Typography>
                            <Typography variant="caption" color="#4CAF50" sx={{ display: 'block', mt: 0.5 }}>
                              ↑ %5 artış
                            </Typography>
                          </Card>
              </Grid2>
                        <Grid2 size={{ xs: 12, md: 4 }}>
                          <Card sx={{
                            borderRadius: 2,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            border: '1px solid #E0E0E0',
                            p: 2,
                            textAlign: 'center'
                          }}>
                            <Typography variant="h4" fontWeight="bold" color="#FF9800" sx={{ mb: 1 }}>
                              4:23
                            </Typography>
                            <Typography variant="body2" color="#666">
                              Ort. Süre
                            </Typography>
                            <Typography variant="caption" color="#F44336" sx={{ display: 'block', mt: 0.5 }}>
                              ↓ %8 azalış
              </Typography>
                          </Card>
                        </Grid2>
                      </Grid2>

                      {/* Detaylı Rapor Tablosu */}
                      <Card sx={{
                        borderRadius: 2,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        border: '1px solid #E0E0E0'
                      }}>
                        <CardContent sx={{ p: 0 }}>
                          <Box sx={{ p: 2, borderBottom: '1px solid #E0E0E0' }}>
                            <Typography variant="h6" fontWeight="600" color="#333">
                              Detaylı Çağrı Raporu
                  </Typography>
                          </Box>
                          
                          {/* Tablo Header */}
                          <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr',
                            gap: 2,
                            p: 2,
                            bgcolor: '#F8F9FA',
                            borderBottom: '1px solid #E0E0E0',
                            fontWeight: 600,
                            color: '#333'
                          }}>
                            <Typography variant="body2">Temsilci</Typography>
                            <Typography variant="body2">Toplam Çağrı</Typography>
                            <Typography variant="body2">Başarılı</Typography>
                            <Typography variant="body2">Başarı Oranı</Typography>
                            <Typography variant="body2">Ort. Süre</Typography>
                          </Box>

                          {/* Tablo Satırları */}
                          {activeAgents.map((agent: any, index: number) => {
                            const agentInfo = users.find((user: any) => user._id === agent._id);
                            const statusInfo = getAgentStatusInfo(agent._id);
                            return (
                              <Box key={agent._id} sx={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr',
                                gap: 2,
                                p: 2,
                                borderBottom: index < activeAgents.length - 1 ? '1px solid #E0E0E0' : 'none',
                                '&:hover': {
                                  bgcolor: '#F8F9FA'
                                },
                                transition: 'background-color 0.2s ease'
                              }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Avatar sx={{
                                    width: 24,
                                    height: 24,
                                    bgcolor: statusInfo?.color || '#666',
                                    fontSize: '0.75rem'
                                  }}>
                                    {agentInfo ? agentInfo.firstName[0] + agentInfo.lastName[0] : 'A'}
                                  </Avatar>
                                  <Typography variant="body2" fontWeight="500">
                                    {agentInfo ? `${agentInfo.firstName} ${agentInfo.lastName}` : 'Bilinmiyor'}
                                  </Typography>
                                </Box>
                                <Typography variant="body2" color="#333">
                                  {agent.totalCalls || 0}
                                </Typography>
                                <Typography variant="body2" color="#4CAF50" fontWeight="500">
                                  {Math.round((agent.totalCalls || 0) * (agent.successRate || 0) / 100)}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" color="#333">
                                    {agent.successRate || 0}%
                                  </Typography>
                                  <Box sx={{
                                    width: 40,
                                    height: 4,
                                    bgcolor: '#E0E0E0',
                                    borderRadius: 2,
                                    overflow: 'hidden'
                                  }}>
                                    <Box sx={{
                                      width: `${agent.successRate || 0}%`,
                                      height: '100%',
                                      bgcolor: (agent.successRate || 0) >= 80 ? '#4CAF50' : 
                                              (agent.successRate || 0) >= 60 ? '#FF9800' : '#F44336'
                                    }} />
                                  </Box>
                                </Box>
                                <Typography variant="body2" color="#333">
                                  {agent.successRate > 0 ? '4:23' : '-'}
                                </Typography>
                              </Box>
                            );
                          })}
            </CardContent>
        </Card>

                      {/* Grafik Raporları */}
                      <Grid2 container spacing={3} sx={{ mt: 3 }}>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                          <Card sx={{
                            borderRadius: 2,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            border: '1px solid #E0E0E0',
                            p: 3
                          }}>
                            <Typography variant="h6" fontWeight="600" sx={{ mb: 2, color: '#333' }}>
                              Günlük Çağrı Trendi
                            </Typography>
                            <Box sx={{
                              height: 200,
                              display: 'flex',
                              alignItems: 'end',
                              justifyContent: 'space-between',
                              px: 2
                            }}>
                              {[65, 78, 82, 75, 88, 92, 85].map((height, index) => (
                                <Box key={index} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                  <Box sx={{
                                    width: 20,
                                    height: height,
                                    bgcolor: '#1976D2',
                                    borderRadius: '2px 2px 0 0',
                                    mb: 1
                                  }} />
                                  <Typography variant="caption" color="#666">
                                    {['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'][index]}
              </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Card>
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                          <Card sx={{
                            borderRadius: 2,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            border: '1px solid #E0E0E0',
                            p: 3
                          }}>
                            <Typography variant="h6" fontWeight="600" sx={{ mb: 2, color: '#333' }}>
                              Çağrı Dağılımı
              </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ width: 12, height: 12, bgcolor: '#4CAF50', borderRadius: 1 }} />
                                  <Typography variant="body2" color="#666">Başarılı Çağrılar</Typography>
                                </Box>
                                <Typography variant="body2" fontWeight="600" color="#4CAF50">128 (%82)</Typography>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ width: 12, height: 12, bgcolor: '#F44336', borderRadius: 1 }} />
                                  <Typography variant="body2" color="#666">Cevapsız Çağrılar</Typography>
                                </Box>
                                <Typography variant="body2" fontWeight="600" color="#F44336">28 (%18)</Typography>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ width: 12, height: 12, bgcolor: '#FF9800', borderRadius: 1 }} />
                                  <Typography variant="body2" color="#666">Beklemede</Typography>
                                </Box>
                                <Typography variant="body2" fontWeight="600" color="#FF9800">12 (%8)</Typography>
                              </Box>
                            </Box>
                          </Card>
                        </Grid2>
                      </Grid2>
            </Box>
                  )}

                  {activeTab === 'ayarlar' && (
                    <Box>
                      <Typography variant="h6" fontWeight="600" sx={{ mb: 3, color: '#333' }}>
                        Ayarlar
                      </Typography>
                      <Typography variant="body1" color="#666">
                        Ayarlar bölümü yakında eklenecek...
            </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Fade>
        </Box>
      </Box>
    </Box>
  );
};

export default SupervisorPanel;
